// PDF Scanner — renders PDF pages to images, sends to Claude Vision API,
// returns LaTeX source code.

import * as pdfjsLib from "pdfjs-dist";
// Vite bundles the worker as a static asset and gives us its URL
import workerSrc from "pdfjs-dist/build/pdf.worker.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

const ANTHROPIC_API_URL = "https://api.anthropic.com/v1/messages";
const MODEL = "claude-haiku-4-5-20251001";
// Render scale — higher = better OCR quality, larger payload
const RENDER_SCALE = 2.0;
// Max pages to send in one API call (to stay within token limits)
const MAX_PAGES_PER_CALL = 6;

/**
 * Render a single PDF page to a base64 PNG string.
 */
async function renderPageToBase64(
    page: pdfjsLib.PDFPageProxy
): Promise<string> {
    const viewport = page.getViewport({ scale: RENDER_SCALE });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d")!;
    await page.render({ canvasContext: ctx, canvas, viewport }).promise;
    return canvas.toDataURL("image/png").split(",")[1];
}

/**
 * Call Claude Vision API with a batch of page images.
 * Returns the raw LaTeX string Claude produces.
 */
async function callClaudeVision(
    pageImages: string[],
    apiKey: string,
    isFirstBatch: boolean
): Promise<string> {
    const content: object[] = [];

    for (let i = 0; i < pageImages.length; i++) {
        if (i > 0) {
            content.push({ type: "text", text: `\n\n[Page ${i + 1}]\n` });
        }
        content.push({
            type: "image",
            source: {
                type: "base64",
                media_type: "image/png",
                data: pageImages[i],
            },
        });
    }

    const instruction = isFirstBatch
        ? `Convert this document to LaTeX source code.
Rules:
- Return ONLY raw LaTeX — no markdown fences, no explanations
- Detect the document class from the layout and content:
  * Two-column physics/APS journal (APS, AIP, PRL, PRB style) → use \\documentclass[aps,twocolumn,reprint]{revtex4-2}
  * Two-column IEEE/engineering paper → use \\documentclass[twocolumn]{IEEEtran}
  * Single-column math paper (AMS style, theorems, definitions) → use \\documentclass{amsart}
  * Standard single-column article → use \\documentclass{article}
  * Default when unsure → use \\documentclass{article}
- Include the detected \\documentclass, appropriate \\usepackage lines, and full document structure
- If the layout is two-column, wrap the body content in \\begin{multicols}{2}...\\end{multicols} (requires \\usepackage{multicol})
- Preserve ALL mathematical equations using proper LaTeX notation ($...$, \\[...\\], \\begin{equation}, \\begin{align}, etc.)
- Preserve section structure (\\section, \\subsection, etc.)
- Preserve theorem/lemma/proof/definition environments
- If the document spans multiple pages, produce one continuous LaTeX document`
        : `Continue converting the remaining pages to LaTeX. Output ONLY the LaTeX body content (no \\documentclass or \\begin{document} — those are already written). Preserve all math, structure, and environments.`;

    content.push({ type: "text", text: instruction });

    if (import.meta.env.DEV) console.log("[Scanner] sending to Claude API, images:", pageImages.length);
    const response = await fetch(ANTHROPIC_API_URL, {
        method: "POST",
        headers: {
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "anthropic-dangerous-direct-browser-access": "true",
            "content-type": "application/json",
        },
        body: JSON.stringify({
            model: MODEL,
            max_tokens: 8192,
            messages: [{ role: "user", content }],
        }),
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({})) as { error?: { message?: string } };
        if (response.status === 401) {
            throw new Error("Invalid API key. Please check your Anthropic API key and try again.");
        }
        if (response.status === 429) {
            throw new Error("Rate limit reached. Please wait a moment and try again.");
        }
        throw new Error(err.error?.message ?? `API error ${response.status}`);
    }

    const data = await response.json() as { content: Array<{ text: string }> };
    return data.content[0].text.trim();
}

export interface ScanProgress {
    stage: "rendering" | "scanning" | "done";
    page: number;
    total: number;
}

/**
 * Main entry point. Scans a PDF file and returns LaTeX source.
 *
 * @param file       The PDF File object from the file input
 * @param apiKey     Anthropic API key
 * @param onProgress Optional callback for progress updates
 */
export async function scanPdfToLatex(
    file: File,
    apiKey: string,
    onProgress?: (p: ScanProgress) => void
): Promise<string> {
    if (import.meta.env.DEV) console.log("[Scanner] loading PDF, size:", file.size);
    // Load PDF
    const arrayBuffer = await file.arrayBuffer();
    if (import.meta.env.DEV) console.log("[Scanner] arrayBuffer byteLength:", arrayBuffer.byteLength);
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const totalPages = pdf.numPages;
    if (import.meta.env.DEV) console.log("[Scanner] PDF loaded, pages:", totalPages);

    // Render all pages to base64 images
    const pageImages: string[] = [];
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        onProgress?.({ stage: "rendering", page: pageNum, total: totalPages });
        const page = await pdf.getPage(pageNum);
        const img = await renderPageToBase64(page);
        if (import.meta.env.DEV) console.log(`[Scanner] rendered page ${pageNum}, base64 length:`, img.length);
        pageImages.push(img);
    }

    // Split into batches and call Claude
    const batches: string[][] = [];
    for (let i = 0; i < pageImages.length; i += MAX_PAGES_PER_CALL) {
        batches.push(pageImages.slice(i, i + MAX_PAGES_PER_CALL));
    }
    if (import.meta.env.DEV) console.log("[Scanner] batches:", batches.length);

    const results: string[] = [];
    for (let b = 0; b < batches.length; b++) {
        const startPage = b * MAX_PAGES_PER_CALL + 1;
        onProgress?.({ stage: "scanning", page: startPage, total: totalPages });
        if (import.meta.env.DEV) console.log(`[Scanner] calling Claude for batch ${b + 1}/${batches.length}`);
        const latex = await callClaudeVision(batches[b], apiKey, b === 0);
        if (import.meta.env.DEV) console.log(`[Scanner] batch ${b + 1} done, latex length:`, latex.length);
        results.push(latex);
    }

    // If more than one batch, the first result is the full doc and subsequent
    // ones are body-only continuations. Merge them before \end{document}.
    if (results.length === 1) {
        return results[0];
    }

    const first = results[0];
    const continuation = results.slice(1).join("\n\n");
    // Insert continuation just before \end{document}
    const endDocIdx = first.lastIndexOf("\\end{document}");
    if (endDocIdx !== -1) {
        return (
            first.slice(0, endDocIdx) +
            "\n\n" +
            continuation +
            "\n\n" +
            first.slice(endDocIdx)
        );
    }
    // Fallback: just concatenate
    return first + "\n\n" + continuation;
}

const API_KEY_STORAGE_KEY = "seetex-anthropic-api-key";

export function getStoredApiKey(): string | null {
    return localStorage.getItem(API_KEY_STORAGE_KEY);
}

export function storeApiKey(key: string): void {
    localStorage.setItem(API_KEY_STORAGE_KEY, key.trim());
}

export function clearApiKey(): void {
    localStorage.removeItem(API_KEY_STORAGE_KEY);
}
